import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const SavedFilterPresets = ({ presets, onLoadPreset, onDeletePreset, onSavePreset, currentFilters }) => {
  const [isAddingPreset, setIsAddingPreset] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [expandedPreset, setExpandedPreset] = useState(null);

  const handleSavePreset = () => {
    if (presetName?.trim()) {
      onSavePreset(presetName?.trim());
      setPresetName('');
      setIsAddingPreset(false);
    }
  };

  const getActiveFiltersCount = (filters) => {
    let count = 0;
    if (filters?.status !== 'all') count++;
    if (filters?.qualification?.length > 0) count++;
    if (filters?.experience !== 'all') count++;
    if (filters?.skills?.length > 0) count++;
    if (filters?.location?.length > 0) count++;
    if (filters?.availability !== 'all') count++;
    if (filters?.matchScore > 0) count++;
    if (filters?.salaryRange?.min > 0 || filters?.salaryRange?.max < 2000000) count++;
    return count;
  };

  const formatSalary = (amount) => {
    if (amount >= 100000) {
      return `₹${(amount / 100000)?.toFixed(1)}L`;
    }
    return `₹${(amount / 1000)?.toFixed(0)}K`;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-card-foreground flex items-center gap-2">
          <Icon name="Bookmark" size={20} />
          Saved Presets
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsAddingPreset(!isAddingPreset)}
          iconName={isAddingPreset ? 'X' : 'Plus'}
        >
          {isAddingPreset ? 'Cancel' : 'New'}
        </Button>
      </div>

      {isAddingPreset && (
        <div className="mb-4 p-3 bg-muted/50 rounded-lg">
          <label className="text-sm font-medium text-foreground mb-2 block">
            Preset Name
          </label>
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="e.g., Senior Developers"
              value={presetName}
              onChange={(e) => setPresetName(e?.target?.value)}
              onKeyPress={(e) => e?.key === 'Enter' && handleSavePreset()}
            />
            <Button
              variant="default"
              size="sm"
              onClick={handleSavePreset}
              disabled={!presetName?.trim()}
            >
              Save
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Current filters will be saved to this preset
          </p>
        </div>
      )}

      {presets?.length === 0 ? (
        <div className="text-center py-6">
          <Icon name="Bookmark" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">
            No saved presets yet
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {presets?.map((preset) => (
            <div
              key={preset?.id}
              className="border border-border rounded-lg p-3 hover:bg-muted/50 transition-smooth"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-card-foreground">
                      {preset?.name}
                    </h4>
                    <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full">
                      {getActiveFiltersCount(preset?.filters)} filters
                    </span>
                  </div>
                  
                  {expandedPreset === preset?.id && (
                    <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                      {preset?.filters?.experience !== 'all' && (
                        <div>• Experience: {preset?.filters?.experience}</div>
                      )}
                      {preset?.filters?.skills?.length > 0 && (
                        <div>• Skills: {preset?.filters?.skills?.join(', ')}</div>
                      )}
                      {preset?.filters?.location?.length > 0 && (
                        <div>• Locations: {preset?.filters?.location?.length} selected</div>
                      )}
                      {preset?.filters?.availability !== 'all' && (
                        <div>• Availability: {preset?.filters?.availability}</div>
                      )}
                      {(preset?.filters?.salaryRange?.min > 0 || preset?.filters?.salaryRange?.max < 2000000) && (
                        <div>
                          • Salary: {formatSalary(preset?.filters?.salaryRange?.min)} - {formatSalary(preset?.filters?.salaryRange?.max)}
                        </div>
                      )}
                      {preset?.filters?.matchScore > 0 && (
                        <div>• Min Match: {preset?.filters?.matchScore}%</div>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setExpandedPreset(expandedPreset === preset?.id ? null : preset?.id)}
                    className="p-1.5 hover:bg-muted rounded transition-smooth"
                    title="View details"
                  >
                    <Icon name={expandedPreset === preset?.id ? 'ChevronUp' : 'ChevronDown'} size={16} />
                  </button>
                  <button
                    onClick={() => onLoadPreset(preset)}
                    className="p-1.5 hover:bg-primary/10 text-primary rounded transition-smooth"
                    title="Load preset"
                  >
                    <Icon name="Download" size={16} />
                  </button>
                  <button
                    onClick={() => onDeletePreset(preset?.id)}
                    className="p-1.5 hover:bg-destructive/10 text-destructive rounded transition-smooth"
                    title="Delete preset"
                  >
                    <Icon name="Trash2" size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SavedFilterPresets;